import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';

import {
  AlternativeCompundsRequest,
  Configuration,
  FoodRequestOptions,
  Menu,
  MenuRequest,
  MenuStage,
  Plan,
  PlanRequest,
  PlanStageCompaund,
  Recipe,
  RecipeForPrint,
  RequestOptions,
  ServerResponse,
  Profile,
  AddToMenuPlanId, MessageInfo,
} from '../models';
import { AccountService } from './account.service';
import { AppConfig } from './app.config';
import { RestService } from './rest.service';
import {PopupService} from '@app/shared/services/popup.service';
import {PopupMessageSummaries, PopupMessageTypes} from '@app/shared/dictionaries';
import {isBoolean} from "util";

@Injectable()
export class PlanningService {
  private needRefreshRecipes = new BehaviorSubject<boolean>(false);

  public mobilePlans: Plan[] = [];
  public fastMenuList = [
    {
      'dayId': 1,
      'name': 'ПН',
      'amount': 0
    }, {
      'dayId': 2,
      'name': 'ВТ',
      'amount': 0
    }, {
      'dayId': 3,
      'name': 'СР',
      'amount': 0
    }, {
      'dayId': 4,
      'name': 'ЧТ',
      'amount': 0
    }, {
      'dayId': 5,
      'name': 'ПТ',
      'amount': 0
    }, {
      'dayId': 6,
      'name': 'СБ',
      'amount': 0
    }, {
      'dayId': 7,
      'name': 'ВС',
      'amount': 0
    }
  ];
  mobileShowProfile = [false, false, false];
  activeProfile = 0;
  activePlanningSubmenu = new BehaviorSubject<string>(null);
  isPrintOn = new BehaviorSubject<boolean>(false);

  constructor(
    private restService: RestService,
    private popupService: PopupService,
    private accountService: AccountService
  ) {
    this.Config = AppConfig.config;
    this.requestOptions.ApiVersion = 'v1';
    this.requestOptions.Id_app = this.Config.AppId;
    this.requestOptions.ManualJSON = 0;
    this.requestOptions.Controller0 = 'Plans';

    this.accountService.getLogOutSubscribtion().subscribe(x => {
      if (x) {
        this.clearData();
      }
    });
  }

  private Config: Configuration;
  private datePicker = new Date();

  private requestOptions = new FoodRequestOptions();
  private plans = new Array<Menu>();
  private profilesMenu: MenuStage[] = [];
  private compoundsForChange: PlanStageCompaund[];
  private compoundsForChangeSubject = new BehaviorSubject<PlanStageCompaund[]>(null);
  private weekRecipes: Recipe[];
  private weekRecipesSubject = new BehaviorSubject<Recipe[]>(null);

  /**Получение недельных планов */
  getWeekPlans() {
    return new Promise<Plan[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        this.requestOptions.Method = 'print';
        this.requestOptions.Page = 1;
        this.requestOptions.PageSize = 1000;
        this.requestOptions.Id_session = cred.Id_Session;
        this.requestOptions.Id_user = cred.Id_User;

        this.restService.get(
          this.requestOptions).then(weeksList => {
            if (weeksList) {
              const response: ServerResponse<Plan> = weeksList;
              resolve(response.Result.Resultset);
            }
          });
      });
    });
  }

  getWeekPlansCurrnet(nWeek: number = null) {
    return new Promise<Plan[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        this.requestOptions.Method = 'print';
        this.requestOptions.Page = 1;
        this.requestOptions.PageSize = 1000;
        this.requestOptions.Id_session = cred.Id_Session;
        this.requestOptions.Id_user = cred.Id_User;

        this.requestOptions.NWeek = nWeek || this.getSelectedWeekNumber();

        this.restService.get(
          this.requestOptions).then(weeksList => {
            if (weeksList) {
              const response: ServerResponse<Plan> = weeksList;
              resolve(response.Result.Resultset);
            }
          });
      });
    });
  }

  /**Обновляет данные о рецептах текущей недели */
  public refreshCurrentWeekRecipes(nWeek: number = null, ManualJsonStatus?: boolean): void {
    this.accountService.getCredentialsPromise().then(cred => {
      const refreshWeekRecipes = new RequestOptions();
      refreshWeekRecipes.Id_session = cred.Id_Session;
      refreshWeekRecipes.Id_app = this.Config.AppId;
      refreshWeekRecipes.ManualJSON = ManualJsonStatus ? 0 : 1;
      refreshWeekRecipes.Controller0 = 'Plans';
      refreshWeekRecipes.PageSize = 1000;
      refreshWeekRecipes.Page = refreshWeekRecipes.Page || 1;
      this.requestOptions.NWeek = nWeek || this.getSelectedWeekNumber();

      refreshWeekRecipes.Section = `Pot/${this.requestOptions.NWeek}`;
      refreshWeekRecipes.Method = 'Print';
      refreshWeekRecipes.ApiVersion = 'v1';

      this.restService.get(refreshWeekRecipes).then(weekRecipes => {
        if (weekRecipes !== null) {
          this.weekRecipes = weekRecipes.Result.Resultset;
          this.weekRecipesSubject.next(this.weekRecipes);
        }
      });
    });
  }

  /**Возвращает observable объект рецептов, использованных на текущей неделе */
  public getWeekRecipes(): Observable<Recipe[]> {
    return this.weekRecipesSubject.asObservable();
  }

  public getSelectedWeekNumber(): number {
    this.requestOptions.NWeek = this.requestOptions.NWeek || this.getWeekNumber();
    return this.requestOptions.NWeek;
  }

  getWeekMenu(week: number = 0, profiles: number[]): Promise<Menu[]> {
    return new Promise<Menu[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new PlanRequest();
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app;
        menu.ManualJSON = this.requestOptions.ManualJSON;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.Id_session = cred.Id_Session;
        menu.Method = 'print';
        menu.Page = 1;
        menu.PageSize = 1000;
        menu.NWeek = week;
        menu.Id_user = cred.Id_User;

        let i = 0;
        profiles.forEach(profile => {
          menu.Id_Profile = profile;
          this.restService.get(menu).then(y => {
            if (y !== null) {
              i++;
              const response: ServerResponse<Menu> = y;
              this.plans = this.plans.concat(response.Result.Resultset);
              if (i === profiles.length) {
                const plans = Array.from(this.plans);
                this.plans = [];
                resolve(plans);
              }
            }
          });
        });
      });
    });
  }

  deletePlan(id: number) {
    this.accountService.getCredentialsPromise().then(cred => {
      const menu = new PlanRequest();
      menu.ApiVersion = this.requestOptions.ApiVersion;
      menu.Id_app = this.requestOptions.Id_app;
      menu.ManualJSON = this.requestOptions.ManualJSON;
      menu.Controller0 = this.requestOptions.Controller0;
      menu.EntityId0 = id;
      menu.Id_session = cred.Id_Session;
      menu.Method = 'delete';

      this.restService.post(menu).then();
    });
  }

  createMenu(week: number, profile: number, name: string) {
    return new Promise<null>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new PlanRequest();
        menu.FullName = name;
        menu.Id_Profile = profile;
        menu.Id_Session = cred.Id_Session;
        menu.Id_User = cred.Id_User;
        menu.NWeek = week;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.Method = 'insert';
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app = this.Config.AppId;
        menu.ManualJSON = this.requestOptions.ManualJSON = 0;

        this.restService.post(menu).then(x => {
          if (x !== null) {
            resolve();
          }
        });
      });
    });
  }

  /**Получение стейджа меню по id планов */
  getMenu(plans: number[]) {
    return new Promise<MenuStage[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new PlanRequest();
        menu.Id_User = cred.Id_User;
        menu.Id_Session = cred.Id_Session;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.Section = 'Stage';
        menu.Method = 'print';
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app;
        menu.ManualJSON = this.requestOptions.ManualJSON;

        let index = 0;
        plans.forEach(x => {
          menu.EntityId0 = x;

          this.restService.get(menu).then(y => {
            if (y !== null && y !== undefined) {
              const response: ServerResponse<MenuStage> = y;
              this.profilesMenu = this.profilesMenu.concat(response.Result.Resultset);
              index++;
              if (index === plans.length) {
                const menus = Array.from(this.profilesMenu);
                this.profilesMenu = [];
                resolve(menus);
              }
            }
          });
        });
      });
    });
  }

  getSingleStage(planId: number, stageId: number) {
    return new Promise<MenuStage>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new PlanRequest();
        menu.Id_User = cred.Id_User;
        menu.Id_Session = cred.Id_Session;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.EntityId0 = planId;
        menu.Controller1 = 'Stage';
        menu.EntityId1 = stageId;
        menu.Method = 'print';
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app;
        menu.ManualJSON = this.requestOptions.ManualJSON;

        this.restService.get(menu).then(y => {
          if (y !== null && y !== undefined) {
            const response: ServerResponse<MenuStage> = y;
            resolve(response.Result.Resultset[0]);
          }
        });
      });
    });
  }

  insertCompound(stage_id: number, id_originProduct: number, id_realProduct: number, newWeight: number, planId?: number, stageId?: number) {
    return new Promise<number>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const newCompoundRequest = new PlanStageCompaund();
        newCompoundRequest.Id_session = cred.Id_Session;
        newCompoundRequest.Controller0 = 'Plans';
        newCompoundRequest.EntityId0 = undefined;
        newCompoundRequest.Controller1 = 'Stage';
        newCompoundRequest.EntityId1 = stage_id;
        newCompoundRequest.Controller2 = 'Compaund';
        newCompoundRequest.EntityId2 = undefined;
        newCompoundRequest.Section = undefined;
        newCompoundRequest.CalculationWeightReal = newWeight;
        newCompoundRequest.Method = 'insert';
        newCompoundRequest.Id_OrigProduct = id_realProduct;
        newCompoundRequest.Id_RealProduct = id_originProduct;
        newCompoundRequest.ApiVersion = this.requestOptions.ApiVersion;
        newCompoundRequest.Id_app = this.requestOptions.Id_app;
        newCompoundRequest.ManualJSON = undefined;

        this.restService.post(newCompoundRequest).then(x => {
          if (x !== null) {
            const response: ServerResponse<string> = x;
            if (planId && stageId) {
              this.getAlternatives(planId, stageId);
            }
            resolve(response.Result.ReturnCode);
          }
        });
      });
    });
  }

  deleteCompound(id_PlanStageCompaund: number) {
    return new Promise<null>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const newCompoundRequest = new PlanStageCompaund();
        newCompoundRequest.Id_session = cred.Id_Session;
        newCompoundRequest.Controller0 = 'Plans';
        newCompoundRequest.EntityId0 = undefined;
        newCompoundRequest.Controller1 = 'Stage';
        newCompoundRequest.EntityId1 = undefined;
        newCompoundRequest.Controller2 = 'Compaund';
        newCompoundRequest.EntityId2 = id_PlanStageCompaund;
        newCompoundRequest.Section = undefined;
        newCompoundRequest.Method = 'delete';
        newCompoundRequest.ApiVersion = this.requestOptions.ApiVersion;
        newCompoundRequest.Id_app = this.requestOptions.Id_app;
        newCompoundRequest.ManualJSON = this.requestOptions.ManualJSON;

        this.restService.post(newCompoundRequest).then(x => {
          if (x !== null) {
            resolve();
          }
        });
      });
    });
  }

  updateCompound(id_PlanStageCompaund: number, id_realProduct: number, newWeight: number, planId?: number, stageId?: number) {
    return new Promise<number>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const newCompoundRequest = new PlanStageCompaund();
        newCompoundRequest.Id_session = cred.Id_Session;
        newCompoundRequest.Controller0 = 'Plans';
        newCompoundRequest.EntityId0 = undefined;
        newCompoundRequest.Controller1 = 'Stage';
        newCompoundRequest.EntityId1 = undefined;
        newCompoundRequest.Controller2 = 'Compaund';
        newCompoundRequest.EntityId2 = id_PlanStageCompaund;
        newCompoundRequest.Section = undefined;
        newCompoundRequest.CalculationWeightReal = newWeight;
        newCompoundRequest.Method = 'update';
        newCompoundRequest.Id_OrigProduct = undefined;
        newCompoundRequest.Id_RealProduct = id_realProduct;
        newCompoundRequest.ApiVersion = this.requestOptions.ApiVersion;
        newCompoundRequest.Id_app = this.requestOptions.Id_app;
        newCompoundRequest.ManualJSON = undefined;

        this.restService.post(newCompoundRequest)
          .then(resp => {
            if (resp !== null) {
              const response: ServerResponse<string> = resp;
              if (planId && stageId) {
                this.getAlternatives(planId, stageId);
              }

              resolve(response.Result.ReturnCode);
            }
          });
      });
    });
  }

  getAlternatives(id_Plan: number, id_Stage: number): Observable<PlanStageCompaund[]> {
    this.accountService.getCredentialsPromise().then(cred => {
      const alternativesRequest = new AlternativeCompundsRequest();
      alternativesRequest.Id_session = cred.Id_Session;
      alternativesRequest.Controller0 = 'Plans';
      alternativesRequest.EntityId0 = id_Plan;
      alternativesRequest.Controller1 = 'Stage';
      alternativesRequest.EntityId1 = id_Stage;
      alternativesRequest.Controller2 = 'Compaund';
      alternativesRequest.EntityId2 = undefined;
      alternativesRequest.Section = undefined;
      alternativesRequest.ChangesOnly = 1;
      alternativesRequest.Method = 'print';
      alternativesRequest.ApiVersion = this.requestOptions.ApiVersion;
      alternativesRequest.Id_app = this.requestOptions.Id_app;
      alternativesRequest.ManualJSON = 0;

      this.restService
        .get(alternativesRequest)
        .then(resp => {
          const response: ServerResponse<PlanStageCompaund> = resp;
          this.compoundsForChange = response.Result.Resultset;
          this.compoundsForChangeSubject.next(this.compoundsForChange);
        });
    });

    return this.compoundsForChangeSubject.asObservable();
  }

  getAlternativesOnce(id_Plan: number, id_Stage: number): Promise<PlanStageCompaund[]> {
    return new Promise<PlanStageCompaund[]>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const alternativesRequest = new AlternativeCompundsRequest();
        alternativesRequest.Id_session = cred.Id_Session;
        alternativesRequest.Controller0 = 'Plans';
        alternativesRequest.EntityId0 = id_Plan;
        alternativesRequest.Controller1 = 'Stage';
        alternativesRequest.EntityId1 = id_Stage;
        alternativesRequest.Controller2 = 'Compaund';
        alternativesRequest.ChangesOnly = 1;
        alternativesRequest.EntityId2 = undefined;
        alternativesRequest.Section = undefined;
        alternativesRequest.Method = 'print';
        alternativesRequest.ApiVersion = this.requestOptions.ApiVersion;
        alternativesRequest.Id_app = this.requestOptions.Id_app;
        alternativesRequest.ManualJSON = 0;
        if (alternativesRequest.Id_session !== null) {
          this.restService.get(alternativesRequest).then(altCompounds => {
            if (altCompounds !== null) {
              // const response: ServerResponse<PlanStageCompaund> = altCompounds;
              this.compoundsForChange = altCompounds.Result.Resultset;
              this.compoundsForChangeSubject.next(this.compoundsForChange);
              resolve(this.compoundsForChange);
            }
          });
        }
      });
    });
  }

  addEating(
    plan: number,
    day: number,
    eating: number,
    recipe: number,
    weight: number,
    stage: number,
    typeMenu: number,
    withoutThrowException?: boolean,
    withoutStateLoading?: boolean
  ) {
    return new Promise<ServerResponse<string>>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new MenuRequest();
        menu.Id_session = cred.Id_Session;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.EntityId0 = plan;
        menu.Section = 'Stage';
        menu.Method = 'insert';
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app;
        menu.ManualJSON = this.requestOptions.ManualJSON;
        menu.N_day = day;
        menu.N_Part = eating;
        menu.Id_Recipe = recipe;
        menu.CalculationWeight = weight;
        menu.Id_Stage = stage ? stage : undefined;
        menu.TypeMenu = typeMenu;

        this.restService.post(menu, withoutThrowException, withoutStateLoading).then(x => {
            const response: ServerResponse<string> = x;
            resolve(response);
        });
      });
    });
  }

  addPlan(week: number, profileId: number): Promise<number> {
    return new Promise<number>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new PlanRequest();
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app;
        menu.ManualJSON = this.requestOptions.ManualJSON;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.Id_session = cred.Id_Session;
        menu.Method = 'insert';
        menu.Id_Profile = profileId;
        menu.NWeek = week;
        menu.Id_user = cred.Id_User;

        this.restService.post(menu).then(x => {
          if (x !== null) {
            const response: ServerResponse<Menu> = x;
            resolve(response.Result.ReturnCode);
          }
        });
      });
    });

  }

  deleteStage(stageId: number): Promise<null> {
    return new Promise(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const menu = new MenuRequest();
        menu.Id_session = cred.Id_Session;
        menu.Controller0 = this.requestOptions.Controller0;
        menu.Controller1 = 'Stage';
        menu.EntityId1 = stageId;
        menu.Method = 'delete';
        menu.ApiVersion = this.requestOptions.ApiVersion;
        menu.Id_app = this.requestOptions.Id_app;
        menu.ManualJSON = this.requestOptions.ManualJSON;

        this.restService.post(menu).then(x => {
          resolve();
        });
      });
    });
  }

  editMenu(menu: MenuStage) {
    return new Promise<string>(resolve => {
      this.accountService.getCredentialsPromise().then(cred => {
        const request = new MenuRequest();
        request.Id_session = cred.Id_Session;
        request.Controller0 = this.requestOptions.Controller0;
        request.Controller1 = 'Stage';
        request.EntityId1 = menu.Id_Stage;
        request.Method = 'update';
        request.ApiVersion = this.requestOptions.ApiVersion;
        request.Id_app = this.requestOptions.Id_app;
        request.ManualJSON = this.requestOptions.ManualJSON;
        request.Id_Recipe = menu.Id_Recipe;
        request.N_day = menu.N_day;
        request.N_Part = menu.N_Part;
        request.CalculationWeight = menu.CalculationWeight;

        this.restService.post(request).then(x => {
          if (x) {
            resolve(x.Result.Message);
          }
        });
      });
    });
  }

  /**Возвращает id недели в формате 'ггггнн' */
  getWeekNumber(customDate: Date = null): number {
    const date = customDate || new Date();
    if (!customDate) {
      date.setDate(date.getDate() + 7);
    }
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    const lengthWeekNumber = weekNumber.toString().length;
    return +`${date.getFullYear()}${lengthWeekNumber === 1 ? 0 : ''}${weekNumber}`;
  }

  getWeekDateRange(nWeek: number): string {
    // tslint:disable-next-line:no-bitwise
    const year = parseInt(nWeek.toFixed().substring(0, 4), 0);
    const week = parseInt(nWeek.toFixed().substring(4, 6), 0);
    const dayOfWeek = new Date(year, 0, week * 7 - 6);
    const firstDate = this.getMondayOfCurrentWeek(dayOfWeek);
    const lastDate = this.getSundayOfCurrentWeek(dayOfWeek);
    return this.getWeekDateRangeString(firstDate, lastDate);
  }

  private getMondayOfCurrentWeek(date: Date) {
    const day = date.getDay();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + (day === 0 ? -6 : 1) - day);
  }
  private getSundayOfCurrentWeek(date: Date) {
    const day = date.getDay();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + (day === 0 ? 0 : 7) - day);
  }

  public getWeekDateRangeString(date1: Date, date2: Date, separator: string = ' – '): string {
    return `${date1.toLocaleDateString()}${separator}${date2.toLocaleDateString()}`;
  }

  public refreshAfterAddingRecipesInModal(): Observable<boolean> {
    return this.needRefreshRecipes.asObservable();
  }

  public refreshRecipesAfterModal(bool: boolean): void {
    this.needRefreshRecipes.next(bool);
  }

  clearData() {
    this.weekRecipes = [];
    this.weekRecipesSubject.next(this.weekRecipes);
  }

  quickAddEating(recipeId: number, dayId: number, weekNumberDef, idTypeOfEating: number, typeMenu: number) {
    return new Promise<ServerResponse<string>>(resolve => {
      const weekNumber = weekNumberDef ? weekNumberDef : this.getSelectedWeekNumber();
      let planId: number;
      const day = (dayId) ? dayId : 1;
      const eatingId = idTypeOfEating ? idTypeOfEating : 1;
      let profile: Profile;
      let profileId: number;

      const promises: Promise<null>[] = [];
      promises.push(
        new Promise<any>(res => {
          this.accountService.getUserProfiles().then(profiles => {
            profile = profiles.sort((a, b) => a.Id_Profile - b.Id_Profile)[this.activeProfile];
            profileId = profile.Id_Profile;

            this.getWeekPlans().then(weeks => {
              if (weeks) {
                const isHavePlanForId = weeks.some(plan => plan.Id_Profile === profileId);
                if (weeks.length && isHavePlanForId) {
                  const filteredWeeks = weeks.filter(plan => plan.NWeek === weekNumber);
                  if (filteredWeeks.length) {
                    const thisPlan = filteredWeeks.filter(prof => prof.Id_Profile === profileId)[0];
                    planId = thisPlan.Id_Plan;
                    res();
                  }
                }

                if (!planId) {
                  this.addPlan(weekNumber, profile.Id_Profile).then(newPlanId => {
                    planId = newPlanId;
                    res();
                  });
                }
              }
            });
          });
        })
      );

      Promise.all(promises).then(() => {
        this.addEating(
          planId,
          day,
          eatingId,
          recipeId,
          profile.PortionWeight === 0 ? 200 : profile.PortionWeight,
          undefined,
          typeMenu
        ).then(resp => {
          this.refreshCurrentWeekRecipes(null, true);
          resolve(resp);
        });
      });
    });
  }

  createArrayOfPlans(profObj, weeks, sortedProfiles, weekNumber): Promise<AddToMenuPlanId> {
    const currentProfile = sortedProfiles[profObj.index];
    const currentWeight = profObj.value;
    const profileId = currentProfile.Id_Profile;
    let planId = 0;

    // проверяем на существование плана
    const isHavePlanForId = weeks.some(plan => plan.Id_Profile === profileId);
    if (weeks.length && isHavePlanForId) {
      const filteredWeeks = weeks.filter(plan => plan.NWeek === weekNumber);
      if (filteredWeeks.length) {
        const thisPlan = filteredWeeks.filter(prof => prof.Id_Profile === profileId)[0];
        planId = thisPlan.Id_Plan;
        return new Promise<AddToMenuPlanId> (resolver => resolver({
          planId: planId,
          weight: currentWeight
        }));
      }
    }

    // создаем план
    if (planId === 0) {
      return this.addPlan(weekNumber, profileId).then(newPlanId => ({
        planId: newPlanId,
        weight: currentWeight
      }));
    }
  }

  modalAddToMenu(arrProfilesIndexAndWeight, arrDaysIndex, arrEatingIndex, recipeId: number, stageId: number, typeMenu: number): Promise<boolean> {
    const weekNumber = this.getSelectedWeekNumber();

    // подготовка данных к каскаду запросов
    const promise: Promise<null> = new Promise<any>(res => {
      this.accountService.getUserProfiles().then(profiles => {
        const sortedProfiles = profiles.sort((a, b) => a.Id_Profile - b.Id_Profile);

        this.getWeekPlans().then(weeks => {
          if (weeks) {
            // создаем массив из promise так как итерируем запрос на сервер
            res(
              arrProfilesIndexAndWeight.map(
                profObj => this.createArrayOfPlans(profObj, weeks, sortedProfiles, weekNumber)
              )
            );
          }
        });
      });
    });

    // ждем обработку промиса и внутреннего массива промисов
    return Promise.resolve(promise).then(x => Promise.all(x).then(arrayOfPlans => {

      // подготавливаем массив с параметрами для запросов
      const arrayOfQueryParams = this._createArrayOfQueryParams(arrayOfPlans, arrDaysIndex, arrEatingIndex);

      // последовательно реализуем каскад запросов
      return this._sequenceAddEating(arrayOfQueryParams, recipeId, stageId, typeMenu).then( res => {
        const typeSuccessOfQuery: number = this._defineSuccessOfQuery(res);
        this._messageGeneration(typeSuccessOfQuery);
        return new Promise<boolean>(exec => exec(typeSuccessOfQuery !== 0));
      }, err => {
        this._createMessage('Ошибка сервера при добавлении', 0);
        return new Promise<boolean>(exec => exec(false));
      });
    }));
  }

  getPlans(weekNumber: number): Promise<Plan[]> {
    return new Promise<Plan[]>(resolve => {
      this.getWeekPlansCurrnet(weekNumber).then(weeks => {
        if (weeks !== null) {
          this.mobilePlans = weeks.filter(plan => plan.NWeek === weekNumber);
          resolve(this.mobilePlans);
        }
      });
    });
  }

  countAmountRecipesInFastMenuList(plan: Plan[], id: number) {
    this.getMenu(plan.map(x => x.Id_Plan)).then(menu => {
      if (menu !== null) {
        menu = menu.sort((a, b) => a.N_Part - b.N_Part);
        menu.map((currentValue) => {
          if (currentValue.Id_Profile === id) {
            const arrayDay = currentValue.N_day - 1;
            this.fastMenuList[arrayDay]['amount']++;
          }
        });
      }
    });
  }

  formRecipeFromStage(stage: Recipe): Recipe {
    const recipe = new Recipe();
    recipe.Id_Stage = stage.Id_Stage;
    recipe.CalculationWeight = stage.CalculationWeight;
    recipe.GarbageWeight = stage.GarbageWeight;
    recipe.Id_Owner = stage.Id_Owner;
    recipe.Id_Recipe = stage.Id_Recipe;
    recipe.Id_Stage = stage.Id_Stage;
    recipe.CalorificValue = stage.CalorificValue;
    recipe.Proteins = stage.Proteins;
    recipe.Fats = stage.Fats;
    recipe.Carbohydrates = stage.Carbohydrates;
    recipe.RecipeChanged = stage.RecipeChanged;
    recipe.FullName = stage.FullName ? stage.FullName : stage.RecipeName;
    recipe.ImagePath = stage.ImagePath;
    recipe.TimeLastModifed = stage.TimeLastModifed;
    return recipe;
  }

  async _sequenceAddEating(arrayOfQueryParams, recipeId: number, stageId: number, typeMenu: number): Promise<ServerResponse<string>[]> {
    const responsesArray: ServerResponse<string>[] = [];
    for (const params of arrayOfQueryParams) {
      responsesArray.push(
        await this.addEating(
          params.planId,
          params.day,
          params.eating,
          recipeId,
          params.weight,
          stageId === 0 ? undefined : stageId,
          typeMenu,
          true,
          true
        )
      );
    }
    return responsesArray;
  }

  _createArrayOfQueryParams(arrayOfPlans, arrDaysIndex, arrEatingIndex) {
    const arrayOfParams = [];
    arrayOfPlans.forEach((profile: AddToMenuPlanId) => {
      arrDaysIndex.forEach(day => {
        arrEatingIndex.forEach(eating => {
          arrayOfParams.push({
            ...profile,
            day,
            eating,
          });
        });
      });
    });
    return arrayOfParams;
  }

  _messageGeneration(type: number): void {
    switch (type) {
      case 0:
        this._createMessage(this.Config.textMessageAfterAddRecipeAboutError, 0);
        break;
      case 1:
        this._createMessage('Рецепты добавлены!', 2);
        this._createMessage(this.Config.textMessageAfterAddRecipeAboutWarn, 1, this.Config.durationWarningOfAddRecipe);
        break;
      case 2:
        this._createMessage('Рецепты добавлены!', 2);
        break;
    }
  }

  _createMessage(text: string, type: number, duration?: number): void {
    const message = new MessageInfo();
    switch (type) {
      case 0:
        message.severity = PopupMessageTypes.Error;
        message.summary = PopupMessageSummaries.Error;
        break;
      case 1:
        message.severity = PopupMessageTypes.Warn;
        message.summary = PopupMessageSummaries.Warn;
        break;
      case 2:
        message.severity = PopupMessageTypes.Success;
        message.summary = PopupMessageSummaries.Success;
        break;
    }
    message.detail = text;
    message.duration = duration;
    this.popupService.showMessage(message);
  }

  /*
  * 0 - сообщение только об ошибке
  * 1 - два сообщения об ошибке и добавлении рецептов
  * 2 - успешное добавление рецептов
  * */
  _defineSuccessOfQuery(res): number {
    const arrayOfSuccessQuery = res.filter(response => response.Result.ReturnCode >= 0);
    if (arrayOfSuccessQuery.length) {
      if (arrayOfSuccessQuery.length === res.length) {
        return 2;
      } else {
        return 1;
      }
    } else {
      return 0;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/planning.service.ts